import React from 'react';
import {useHistory} from 'react-router-dom';
import {History} from 'history';
import {useForm, useField, FieldRenderProps} from 'react-final-form-hooks';
import wrap from 'wraptile';
import {curry} from 'ramda';

import {UsersPayload} from '@pluto/users/UsersTypes';
import {isValidPlutoEmail, isValidEmail} from '@pluto/validate-pluto-email';
import {adminRoutes} from 'App/AppRouter/routes';
import {ValidationErrors} from 'final-form';
import UserAdd from './UserAdd';
import {getRoleFilter} from '../UserDetails/UserDetailsContainer';

interface UserAddContainerProps {
    saveNewUser: () => void;
    users: UsersPayload;
}

export interface UserFieldValues {
    email: string;
    roles?: string[];
}

export interface ValidationValues {
    email: string;
    roles: string[];
}

const onSubmit = () => {};

export enum ErrorMessages {
    plutoEmailErrorMessage = 'Only emails ending in pluto.tv are permitted.',
    invalidEmailErrorMessage = 'Enter valid email address.',
    existingEmailMessage = 'This email is already registered.',
    roleErrorMessage = 'At least 1 role must be assigned.'
}

export default function UserAddContainer(props: UserAddContainerProps) {
    const {
        saveNewUser,
        users,
    } = props;
    const history = useHistory();
    const {
        form,
        valid,
        errors,
    } = useForm({
        onSubmit,
        validate,
    });
    
    const formValues = form.getState().values;
    const onSaveHandler = saveHandler({
        saveNewUser,
        formValues,
        history,
    });
    
    const emailField = useField('email', form);
    const rolesField = useField('roles', form);
    const pushRouteToHistory = onHistoryRoutePush(
        history,
    );
    const plutoEmailErrorMessage = getErrorMessage(errors, ErrorMessages.plutoEmailErrorMessage);
    const roleErrorMessage = getErrorMessage(errors, ErrorMessages.roleErrorMessage);
    const invalidEmailErrorMessage = getErrorMessage(errors, ErrorMessages.invalidEmailErrorMessage);
    const emailExistsMessage = checkForExistingEmail(emailField.input.value, users);
    const isNotValid = checkIfInvalid(valid, emailExistsMessage);
    
    return (
        <UserAdd
            pushRouteToHistory={pushRouteToHistory}
            emailField={emailField}
            isNotValid={isNotValid}
            plutoEmailErrorMessage={plutoEmailErrorMessage}
            invalidEmailErrorMessage={invalidEmailErrorMessage}
            onChangeRole={onChangeRole}
            rolesField={rolesField}
            roleErrorMessage={roleErrorMessage}
            emailExistsMessage={emailExistsMessage}
            onSaveHandler={onSaveHandler}
        />
    );
}

export const validate = (values: UserFieldValues) => {
    const {
        email,
        roles,
    } = values;
    const plutoEmailErrorText = [ErrorMessages.plutoEmailErrorMessage];
    const invalidEmailErrorText = [ErrorMessages.invalidEmailErrorMessage];
    const roleErrorText = [ErrorMessages.roleErrorMessage];
    const noEmail = '';
    
    if (!email) {
        return [noEmail];
    }
    
    if (email && !isValidEmail(email)) {
        return invalidEmailErrorText;
    }
    
    if (email && !isValidPlutoEmail(email)) {
        return plutoEmailErrorText;
    }
    
    if (email && !roles) {
        return roleErrorText;
    }
    
    if (email && roles?.length === 0) {
        return roleErrorText;
    }
    
    return [];
};

export const checkIfInvalid = (valid: boolean, emailExistsMessage: string) => !valid || emailExistsMessage.length > 0;

export const onHistoryRoutePush = wrap((history: History) => {
    history.push(adminRoutes.users);
});

export const saveHandler = wrap((params: any) => {
    const {
        saveNewUser,
        formValues,
        history,
    } = params;
    const {
        email,
        roles,
    } = formValues;
    const body = {
        email,
        roles,
    };
    
    saveNewUser({
        body,
    });
    history.push(adminRoutes.users);
});

const filterEmail = curry((email, user) => {
    return user.email === email;
});

export const checkForExistingEmail = (email: string, users: UsersPayload) => {
    const res = users.rows.filter(filterEmail(email));
    
    if (res.length)
        return ErrorMessages.existingEmailMessage;
    
    return '';
};

export const isEqual = curry((error: string, message: string): boolean => error === message);

export const getErrorMessage = (errors: ValidationErrors, message: string): string[] => {
    return Object.values(errors).filter(isEqual(message));
};

export const onChangeRole = wrap((roleField: FieldRenderProps, role: string) => {
    const {input} = roleField;
    const {
        value,
        onChange,
    } = input;
    
    if (value.includes(role)) {
        onChange(value.filter(getRoleFilter(role)));
    } else {
        onChange([...value, role]);
    }
});
